<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import { toBool } from "../../../../utils/CommonFunctions";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import GroupedSelect from "../../../../components/ui/form/GroupedSelect.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import PhoneInput from "../../../../components/ui/form/PhoneInput.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import CustomerAddressListView from "./address/CustomerAddressListView.vue";
import { PosStation } from "../../../../types/models";
import Buttons from "../../../../components/ui/form/Buttons.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  ignorePermission?: boolean;
  listCreateName?: string;
  minimized?: boolean;
  posStation?: PosStation | undefined;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  id: props.data?.id || "",
  logo: props.data?.logo || "",
  name: props.data?.name || props.listCreateName || "",
  customer_types_id: props.data?.customer_types_id || "",
  customer_groups_id: props.data?.customer_groups_id || "",
  registration_no: props.data?.registration_no || "",
  tax_registration_no: props.data?.tax_registration_no || "",
  address: props.data?.address || "",
  buildings_id: props.data?.buildings_id || "",
  countrys_id: props.data?.countrys_id || "",
  states_id: props.data?.states_id || "",
  citys_id: props.data?.citys_id || "",
  roads_id: props.data?.roads_id || "",
  mobile: props.data?.mobile || "",
  email: props.data?.email || "",
  url: props.data?.url || "",
  price_lists_id: props.data?.price_lists_id || "",
  credit_terms_id: props.data?.credit_terms_id || "",
  payment_types_id: props.data?.payment_types_id || "",
  credit_limit: props.data?.credit_limit || 0,
  sales_reps_id: props.data?.sales_reps_id || "",
  accounts_receivable_id: props.data?.accounts_receivable_id || "",
  send_invoice_via: props.data?.send_invoice_via || "",
  auto_reminder: props.data?.auto_reminder || false,
  first_reminder: props.data?.first_reminder || 0,
  second_reminder: props.data?.second_reminder || 0,
  third_reminder: props.data?.third_reminder || 0,
  active: props.data?.active || true,
  uid: props.data?.uid || "",
  addresses: props.data?.addresses || [],
  // Storefront login accounts eager-loaded by CustomersController::record.
  // Declared here so `setFormData` (called after the record fetch) actually
  // assigns it onto formData — undeclared keys are dropped silently.
  customer_users: props.data?.customer_users || [],
});

setFieldRules("name", [required("Name is required")]);
setFieldRules("customer_types_id", [required("Customer Type is required")]);
setFieldRules("mobile", [required("Mobile No is required")]);
setFieldRules("credit_terms_id", [required("Credit Term is required")]);
setFieldRules("credit_limit", [required("Credit Limit is required")]);
setFieldRules("accounts_receivable_id", [
  required("Receivable Account is required"),
]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const handleGetRecord = async () => {
  if (!props.data) return;
  const params = {
    id: props.data?.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    console.log("setFormData", response.data);
    setFormData(response.data);
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  min.value = props.minimized || false;
  console.log("props.mode", props.mode);
  console.log('props.data', props.data);
  if (props.mode == "create") {
    resetForm();
  } else if (props.data) {
    handleGetRecord();
  }
});

const handleCustomerType = (data: any) => {
  if (props.mode != "create") return;
  if (data) {
    fields.value.price_lists_id.value = data.price_lists_id;
    fields.value.credit_terms_id.value = data.credit_terms_id;
    fields.value.payment_types_id.value = data.payment_types_id;
    fields.value.credit_limit.value = data.credit_limit;
    fields.value.accounts_receivable_id.value = data.accounts_receivable_id;
    fields.value.send_invoice_via.value = data.send_invoice_via;
    fields.value.auto_reminder.value = toBool(data.auto_reminder);
    fields.value.first_reminder.value = data.first_reminder;
    fields.value.second_reminder.value = data.second_reminder;
    fields.value.third_reminder.value = data.third_reminder;
  }
};
const sendInvoiceViaList = [
  { id: "Print", name: "Print" },
  { id: "Email", name: "Email" },
];
const min = ref(false);
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :ignorePermission="ignorePermission"
    :tabList="[]"
    :formData="formData"
    :showSaveNew="!min"
    :showSave="!min"
    @save="handleSubmit"
    @close="$emit('close')"
  >
  <template #header>
    <Buttons
          v-if="min"
          caption="More Options"
          icon="Expand"
          iconColor="blue"
          @click="min = !min"
          id="quick-tour"
        />
    </template>
    <template #form>
      <div v-if="min">
      <div class="grid md:grid-cols-3 gap-6">
        <SearchSelect
            v-model="fields.customer_types_id.value"
            label="Customer Type"
            end-point="revenue/master-data/customer-types"
            :readonly="isReadOnly"
            :error="fields.customer_types_id.error"
            @select="handleCustomerType"
            :allowNewRecord="true"
          />
        <PhoneInput
            v-model="fields.mobile.value"
            label="Mobile"
            :readonly="isReadOnly"
            :error="fields.mobile.touched ? fields.mobile.error : null"
            @blur="fields.mobile.touched = true"
          />
          <TextInput
            v-model="fields.name.value"
            label="Name"
            :readonly="isReadOnly"
            :error="fields.name.error"
          />
          <SearchSelect
            v-model="fields.countrys_id.value"
            label="Country"
            end-point="system/master-data/countrys"
            :readonly="isReadOnly"
            :error="
              fields.countrys_id.touched ? fields.countrys_id.error : null
            "
            @blur="fields.countrys_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.states_id.value"
            label="State"
            :hasExtra="true"
            optionLabel="states"
            :extra="fields.countrys_id.value"
            end-point="system/master-data/states"
            :readonly="isReadOnly"
            :error="fields.states_id.touched ? fields.states_id.error : null"
            @blur="fields.states_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.citys_id.value"
            label="City"
            :hasExtra="true"
            :extra="fields.states_id.value"
            end-point="system/master-data/citys"
            :readonly="isReadOnly"
            :error="fields.citys_id.touched ? fields.citys_id.error : null"
            optionLabel="citys"
            @blur="fields.citys_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.roads_id.value"
            label="Road"
            optionLabel="roads"
            :hasExtra="true"
            :extra="fields.citys_id.value"
            end-point="system/master-data/roads"
            :readonly="isReadOnly"
            :error="fields.roads_id.touched ? fields.roads_id.error : null"
            @blur="fields.roads_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.buildings_id.value"
            label="Building"
            :hasExtra="true"
            optionLabel="buildings"
            :remote="true"
            :extra="fields.citys_id.value"
            end-point="system/master-data/buildings"
            :readonly="isReadOnly"
            :error="
              fields.buildings_id.touched ? fields.buildings_id.error : null
            "
            @blur="fields.buildings_id.touched = true"
            :allowNewRecord="true"
          />

          <TextAreaInput
            v-model="fields.address.value"
            label="Address"
            :rows="3"
            :readonly="isReadOnly"
            :error="fields.address.touched ? fields.address.error : null"
            @blur="fields.address.touched = true"
          />
        </div>
      </div>
      <div class="grid md:grid-cols-1 gap-6" v-else>
        <div>
          <FileUpload
            accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
            :max-size="5"
            :multiple="false"
            label="Logo"
            :readonly="isReadOnly"
            :image="true"
            path="brands"
            v-model="fields.logo.value"
          />
        </div>
        <div class="grid md:grid-cols-2 gap-6">
          <TextInput
            v-model="fields.name.value"
            label="Name"
            :readonly="isReadOnly"
            :error="fields.name.error"
          />
        </div>
        <div class="grid md:grid-cols-2 gap-6">
          <SearchSelect
            v-model="fields.customer_types_id.value"
            label="Customer Type"
            end-point="revenue/master-data/customer-types"
            :readonly="isReadOnly"
            :error="fields.customer_types_id.error"
            @select="handleCustomerType"
            :allowNewRecord="true"
          />

          <SearchSelect
            v-model="fields.customer_groups_id.value"
            label="Customer Group"
            end-point="revenue/master-data/customer-groups"
            :readonly="isReadOnly"
            :error="fields.customer_groups_id.error"
            :allowNewRecord="true"
          />

          <TextInput
            v-model="fields.registration_no.value"
            label="Registration No"
            :readonly="isReadOnly"
            :error="
              fields.registration_no.touched
                ? fields.registration_no.error
                : null
            "
            @blur="fields.registration_no.touched = true"
          />

          <TextInput
            v-model="fields.tax_registration_no.value"
            label="Tax Identification No"
            :readonly="isReadOnly"
            :error="
              fields.tax_registration_no.touched
                ? fields.tax_registration_no.error
                : null
            "
            @blur="fields.tax_registration_no.touched = true"
          />

          <SearchSelect
            v-model="fields.countrys_id.value"
            label="Country"
            end-point="system/master-data/countrys"
            :readonly="isReadOnly"
            :error="
              fields.countrys_id.touched ? fields.countrys_id.error : null
            "
            @blur="fields.countrys_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.states_id.value"
            label="State"
            :hasExtra="true"
            optionLabel="states"
            :extra="fields.countrys_id.value"
            end-point="system/master-data/states"
            :readonly="isReadOnly"
            :error="fields.states_id.touched ? fields.states_id.error : null"
            @blur="fields.states_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.citys_id.value"
            label="City"
            :hasExtra="true"
            :extra="fields.states_id.value"
            end-point="system/master-data/citys"
            :readonly="isReadOnly"
            :error="fields.citys_id.touched ? fields.citys_id.error : null"
            optionLabel="citys"
            @blur="fields.citys_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.roads_id.value"
            label="Road"
            optionLabel="roads"
            :hasExtra="true"
            :extra="fields.citys_id.value"
            end-point="system/master-data/roads"
            :readonly="isReadOnly"
            :error="fields.roads_id.touched ? fields.roads_id.error : null"
            @blur="fields.roads_id.touched = true"
            :allowNewRecord="true"
          />

          <GroupedSelect
            v-model="fields.buildings_id.value"
            label="Building"
            :hasExtra="true"
            optionLabel="buildings"
            :remote="true"
            :extra="fields.citys_id.value"
            end-point="system/master-data/buildings"
            :readonly="isReadOnly"
            :error="
              fields.buildings_id.touched ? fields.buildings_id.error : null
            "
            @blur="fields.buildings_id.touched = true"
            :allowNewRecord="true"
          />

          <TextAreaInput
            v-model="fields.address.value"
            label="Address"
            :rows="3"
            :readonly="isReadOnly"
            :error="fields.address.touched ? fields.address.error : null"
            @blur="fields.address.touched = true"
          />

          <PhoneInput
            v-model="fields.mobile.value"
            label="Mobile"
            :readonly="isReadOnly"
            :error="fields.mobile.touched ? fields.mobile.error : null"
            @blur="fields.mobile.touched = true"
          />

          <TextInput
            v-model="fields.email.value"
            label="Email"
            :readonly="isReadOnly"
            :error="fields.email.touched ? fields.email.error : null"
            @blur="fields.email.touched = true"
          />

          <TextInput
            v-model="fields.url.value"
            label="Url"
            :readonly="isReadOnly"
            :error="fields.url.touched ? fields.url.error : null"
            @blur="fields.url.touched = true"
          />

          <SearchSelect
            v-model="fields.price_lists_id.value"
            label="Price List"
            end-point="finance/master-data/price-lists"
            :readonly="isReadOnly"
            :error="
              fields.price_lists_id.touched ? fields.price_lists_id.error : null
            "
            @blur="fields.price_lists_id.touched = true"
            :allowNewRecord="true"
          />

          <SearchSelect
            v-model="fields.credit_terms_id.value"
            label="Credit Term"
            end-point="finance/master-data/credit-terms"
            :readonly="isReadOnly"
            :error="
              fields.credit_terms_id.touched
                ? fields.credit_terms_id.error
                : null
            "
            @blur="fields.credit_terms_id.touched = true"
            :allowNewRecord="true"
          />

          <SearchSelect
            v-model="fields.payment_types_id.value"
            label="Payment Type"
            end-point="finance/master-data/payment-types"
            :readonly="isReadOnly"
            :error="
              fields.payment_types_id.touched
                ? fields.payment_types_id.error
                : null
            "
            @blur="fields.payment_types_id.touched = true"
            :allowNewRecord="true"
          />

          <NumberInput
            v-model="fields.credit_limit.value"
            label="Credit Limit"
            :readonly="isReadOnly"
            :min="0"
            :error="
              fields.credit_limit.touched ? fields.credit_limit.error : null
            "
            @blur="fields.credit_limit.touched = true"
          />

          <SearchSelect
            v-model="fields.accounts_receivable_id.value"
            label="Receivable Account"
            end-point="finance/accounting/chart-of-accounts"
            extra="Receivables"
            :readonly="isReadOnly"
            :error="
              fields.accounts_receivable_id.touched
                ? fields.accounts_receivable_id.error
                : null
            "
            @blur="fields.accounts_receivable_id.touched = true"
          />

          <SearchSelect
            v-model="fields.sales_reps_id.value"
            label="Sales Reps"
            end-point="revenue/master-data/sales-reps"
            :readonly="isReadOnly"
            :allow-new-record="true"
            :error="
              fields.sales_reps_id.touched ? fields.sales_reps_id.error : null
            "
            @blur="fields.sales_reps_id.touched = true"
          />

          <SearchSelect
            v-model="fields.send_invoice_via.value"
            label="Send Invoice by"
            :options="sendInvoiceViaList"
            :readonly="isReadOnly"
            :error="
              fields.send_invoice_via.touched
                ? fields.send_invoice_via.error
                : null
            "
            @blur="fields.send_invoice_via.touched = true"
          />

          <CheckInput
            v-model="fields.auto_reminder.value"
            :readonly="isReadOnly"
            label="Auto Reminder"
            :error="
              fields.auto_reminder.touched ? fields.auto_reminder.error : null
            "
            @blur="fields.auto_reminder.touched = true"
          />

          <NumberInput
            v-model="fields.first_reminder.value"
            label="First Reminder (days)"
            :readonly="isReadOnly && fields.auto_reminder.value == true"
            :min="0"
            :error="
              fields.first_reminder.touched ? fields.first_reminder.error : null
            "
            @blur="fields.first_reminder.touched = true"
          />

          <NumberInput
            v-model="fields.second_reminder.value"
            label="Second Reminder (days)"
            :readonly="isReadOnly && fields.auto_reminder.value == true"
            :min="0"
            :error="
              fields.second_reminder.touched
                ? fields.second_reminder.error
                : null
            "
            @blur="fields.second_reminder.touched = true"
          />

          <NumberInput
            v-model="fields.third_reminder.value"
            label="Third Reminder (days)"
            :readonly="isReadOnly && fields.auto_reminder.value == true"
            :min="0"
            :error="
              fields.third_reminder.touched ? fields.third_reminder.error : null
            "
            @blur="fields.third_reminder.touched = true"
          />

          <CheckInput
            v-model="fields.active.value"
            :readonly="isReadOnly"
            label="Active"
            :error="fields.active.touched ? fields.active.error : null"
            @blur="fields.active.touched = true"
          />
        </div>

        <div>
          <CustomerAddressListView
            :permissions="permissions"
            :customer="formData"
            v-if="mode !== 'create'"
          />
        </div>

        <!--
          Storefront login accounts bound to this customer (`customer_users`).
          Read-only here — account creation happens on the storefront's own
          registration flow. Hidden in `create` mode because the parent row
          must exist before there can be any login accounts attached.
        -->
        <div v-if="mode !== 'create' && Array.isArray(formData.customer_users)">
          <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">
            Registered users
            <span class="text-xs font-normal text-gray-500">({{ formData.customer_users.length }})</span>
          </h3>
          <p v-if="formData.customer_users.length === 0" class="text-sm text-gray-500 italic">
            No storefront users have signed up under this customer yet.
          </p>
          <div v-else class="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th class="px-3 py-2 text-left">Name</th>
                  <th class="px-3 py-2 text-left">Email</th>
                  <th class="px-3 py-2 text-left">Mobile</th>
                  <th class="px-3 py-2 text-left">Role</th>
                  <th class="px-3 py-2 text-center">Owner</th>
                  <th class="px-3 py-2 text-left">Last login</th>
                  <th class="px-3 py-2 text-center">Active</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="u in formData.customer_users" :key="u.id" class="text-gray-700 dark:text-gray-300">
                  <td class="px-3 py-2 font-medium">{{ u.name }}</td>
                  <td class="px-3 py-2">
                    {{ u.email }}
                    <span v-if="u.email_verified_at" class="text-xs text-green-600 ml-1">✓</span>
                  </td>
                  <td class="px-3 py-2">{{ u.mobile || '—' }}</td>
                  <td class="px-3 py-2">{{ u.role || '—' }}</td>
                  <td class="px-3 py-2 text-center">
                    <span v-if="toBool(u.is_owner)" class="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Owner</span>
                    <span v-else>—</span>
                  </td>
                  <td class="px-3 py-2 text-xs">
                    <template v-if="u.last_logged_in">
                      {{ new Date(u.last_logged_in).toLocaleString() }}
                    </template>
                    <template v-else>—</template>
                  </td>
                  <td class="px-3 py-2 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full"
                      :class="toBool(u.active) ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'"
                    >{{ toBool(u.active) ? 'Active' : 'Inactive' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </ModalFormTemplate>
</template>
